import { defineStore } from 'pinia'
import { ref } from 'vue'
import { memberAssetsApi, memberProfileApi } from '@/api'
import type { UserAssets, UserProfile } from '@/api'
import { setCustomerServiceUserInfo } from '@/utils/customerService'
import { useSessionStore } from './session'

/**
 * 用户资产管理 Store
 */
export const useAssetsStore = defineStore(
  'UserInfo',
  () => {
    // 用户资产数据
    const userAssets = ref<UserAssets | undefined>(undefined)
    const assetsLoading = ref(false)
    const assetsError = ref(false)
    const assetsErrorMsg = ref<Error | null>(null)
    const currency = ref<string>('LB')

    // 用户信息数据
    const userProfile = ref<UserProfile | undefined>(undefined)
    const profileLoading = ref(false)
    const profileError = ref(false)
    const profileErrorMsg = ref<Error | null>(null)

    /**
     * 手动刷新用户资产
     */
    const refreshAssets = async () => {
      const sessionStore = useSessionStore()
      if (!sessionStore.sessionId) return

      assetsLoading.value = true
      assetsError.value = false
      assetsErrorMsg.value = null

      try {
        const response = await memberAssetsApi()
        userAssets.value = response.data
        currency.value = response.data.currency
        return { data: response.data }
      } catch (error) {
        assetsError.value = true
        assetsErrorMsg.value = error as Error
        throw error
      } finally {
        assetsLoading.value = false
      }
    }

    /**
     * 手动刷新用户信息
     */
    const refreshProfile = async () => {
      // const sessionStore = useSessionStore()
      // if (!sessionStore.sessionId) return

      profileLoading.value = true
      profileError.value = false
      profileErrorMsg.value = null

      try {
        const response = await memberProfileApi()
        userProfile.value = response.data

        // 更新客服访客信息
        setCustomerServiceUserInfo(response.data)

        return { data: response.data }
      } catch (error) {
        profileError.value = true
        profileErrorMsg.value = error as Error
        throw error
      } finally {
        profileLoading.value = false
      }
    }

    /**
     * 刷新所有用户数据
     */
    const refreshAll = async () => {
      await Promise.all([refreshAssets(), refreshProfile()])
    }

    const isSelf = (uid: number) => {
      return userProfile.value?.id == uid
    }

    /**
     * 清空所有用户数据（登出时调用）
     */
    const clear = () => {
      userAssets.value = undefined
      assetsLoading.value = false
      assetsError.value = false
      assetsErrorMsg.value = null
      currency.value = 'LB'

      userProfile.value = undefined
      profileLoading.value = false
      profileError.value = false
      profileErrorMsg.value = null
    }

    return {
      // 资产相关
      userAssets,
      currency,
      assetsLoading,
      assetsError,
      assetsErrorMsg,
      refreshAssets,

      // 用户信息相关
      userProfile,
      profileLoading,
      profileError,
      profileErrorMsg,
      refreshProfile,

      // 通用方法
      refreshAll,
      isSelf,
      clear,
    }
  },
  {
    persist: {
      // 只持久化必要的数据，不持久化加载状态和错误信息
      pick: ['userAssets', 'userProfile'],
    },
  },
)
